import {
  AuthToken,
  ChatRequest,
  ChatResponse,
  ConversationListResponse,
  ConversationSummary,
  ConversationTurnsResponse,
  LoginRequest,
  RegisterRequest,
  User,
} from "@/lib/types";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8010";
const REQUEST_TIMEOUT_MS = 30_000;

class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  token?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
  } catch (error) {
    const isAbort = error instanceof DOMException && error.name === "AbortError";
    const detail = isAbort
      ? `Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s. The backend may be restarting — please try again in a moment.`
      : `Cannot reach API at ${API_BASE_URL}.`;
    throw new ApiError(detail, 0);
  } finally {
    clearTimeout(timeoutId);
  }

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const detail = payload?.detail || payload?.message || "Request failed";
    throw new ApiError(detail, response.status);
  }

  return payload as T;
}

export function login(input: LoginRequest): Promise<AuthToken> {
  return request<AuthToken>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function register(input: RegisterRequest): Promise<User> {
  return request<User>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getCurrentUser(token: string): Promise<User> {
  return request<User>("/api/auth/me", { method: "GET" }, token);
}

export function chat(input: ChatRequest, token: string): Promise<ChatResponse> {
  return request<ChatResponse>("/api/chat", {
    method: "POST",
    body: JSON.stringify(input),
  }, token);
}

export function listConversations(token: string): Promise<ConversationListResponse> {
  return request<ConversationListResponse>("/api/conversations", { method: "GET" }, token);
}

export function createConversation(token: string, title?: string): Promise<ConversationSummary> {
  return request<ConversationSummary>(
    "/api/conversations",
    {
      method: "POST",
      body: JSON.stringify({ title }),
    },
    token,
  );
}

export function getConversationTurns(token: string, conversationId: string): Promise<ConversationTurnsResponse> {
  return request<ConversationTurnsResponse>(`/api/conversations/${conversationId}/turns`, { method: "GET" }, token);
}

export { ApiError, API_BASE_URL };
